import { load } from "../../common/js/localStorage"
import { omit } from "lodash"
import Bugsnag from "@bugsnag/js"
import { initPricingHelpers } from "./pricing"
import { TrackingManager } from "./services/tracking/trackingManager"
import { isFunnelFoxPipeline } from "./funnelFox"
import { getOrderByToken, getOrderByFFSessionToken } from "./api"

export const LOCAL_STORAGE_KEY = "data"
export const INVALID_DOMAIN_CODE = "invalid_domain"
export const EMAIL_VALIDATION_ERROR = "email: Invalid email"
export const EMAIL_VALIDATION_CODE = "email_verification_failed"

export default ({ getInitialValues, customOfferFieldsKeysAdapter }) => ({
  internalTracker: null,

  init() {
    const data = load(LOCAL_STORAGE_KEY)

    // Init pricing helpers with naming adapter for custom offers fields data.
    // This is needed for consistent and backward-compatible common implementation,
    // as each pipe may have differently named offers fields for the same data.
    this.customOfferFieldsKeysAdapter = customOfferFieldsKeysAdapter
    this.pricing = initPricingHelpers(customOfferFieldsKeysAdapter)

    // Init internal tracking manager to be available everywhere globally
    this.internalTracker = new TrackingManager(
      window.trackingConfig.enabledTrackingServices,
      pipelineConfig,
    )

    if (data && data.values) {
      this.values = {
        // Apply initial values at first, in case if new code version
        // added new props, but user got old version in local storage
        ...getInitialValues(),
        ...data.values,
      }
    } else {
      this.values = getInitialValues()
    }

    if (!this.values?.activeOffer) {
      const pipelineOffers = pipelineConfig.offers
      const defaultOffer = pipelineOffers.find(
        (offer) => offer.customData.default_select === "true",
      )

      this.values.activeOffer = defaultOffer || pipelineOffers[0]
    }

    this.storeAnalytics()

    Alpine.effect(() => {
      this.writeToLocalStorage(this.values)
    })
  },

  storeAnalytics() {
    const analyticsData = {}
    const cookiesData = {}

    analyticsData.domain = document.location.host

    // Add cookies - _ga, _gid, _fbc, _fbp, ...
    document.cookie.split("; ").forEach((item) => {
      const [key, value] = item.split("=")
      analyticsData[key] = value
      cookiesData[key] = value
    })

    // Add search params - fbclid, utm_*, ...
    const url = new URL(document.location)
    url.searchParams.forEach((value, key) => {
      analyticsData[key] = value
    })

    // Add FraudNet data
    analyticsData["fraud_net_session_id"] = localStorage.getItem("fraud_net_session_id") || ""

    // Add global data for Payments (override only if values are present due to init on each page)
    const queryString = window.location.search
    if (queryString) {
      analyticsData["query_string"] = queryString
    }
    if (cookiesData && Object.keys(cookiesData).length > 0) {
      analyticsData.cookies = { ...(analyticsData.cookies || {}), ...cookiesData }
    }

    this.values = { ...this.values, ...analyticsData }
  },

  writeToLocalStorage(data) {
    window.localStorage.setItem(
      `${pipelineConfig.pathPrefix}:${LOCAL_STORAGE_KEY}`,
      JSON.stringify({ values: data }),
    )
  },

  // Needed so that head.njk can access the function
  isFunnelFoxPipe() {
    return isFunnelFoxPipeline()
  },

  resetLocalStorage() {
    this.values = getInitialValues()
    this.storeAnalytics()
  },

  isMetric() {
    return this.values.measurementSystem === "metric"
  },

  isImperial() {
    return this.values.measurementSystem === "imperial"
  },

  goToLogin() {
    const loginUrl = this.values.loginUrl
    this.values = getInitialValues()
    window.location = loginUrl || pipelineConfig.loginUrl
  },

  // Saves purchased basic offer, cause during upsells the active offer is overwritten
  savePurchasedBasicOffer() {
    if (!this.values.purchasedBasicOffer) {
      this.values.purchasedBasicOffer = this.values.activeOffer
    }
  },

  // Restores purchased basic offer, cause during upsells the active offer is overwritten
  restorePurchasedBasicOffer() {
    if (this.values.purchasedBasicOffer) {
      this.values.activeOffer = this.values.purchasedBasicOffer
      this.values.purchasedBasicOffer = null
    }
  },

  isAbandonedBasket() {
    return pipelineConfig.variants[pipelineConfig.variant].isAbandonedBasket
  },

  // Saves the needed data to the DB via 'order' api
  async storeCheckoutData({ checkoutSession, isPreloaded }) {
    this.values.checkout = checkoutSession
    this.values.initiateCheckoutAt = new Date().toISOString()

    // Keep flag to distinguish preloaded session if needed (in legacy pipes)
    this.values.isCheckoutPreloaded = isPreloaded

    if (!isPreloaded) {
      this.values.visitCheckoutAt = new Date().toISOString()
      await this.push()
    }
  },

  async pull() {
    if (this.values.token || this.values.ffSessionId) {
      try {
        let res
        let omitKeys = []

        // Trigger only if we have ffSessionId and ffInitialFetch is true
        if (isFunnelFoxPipeline() && this.values.ffInitialFetch) {
          omitKeys = ["activeOffer", "ffInitialFetch"]
          res = await getOrderByFFSessionToken(this.values.ffSessionId)
        } else if (this.values.token) {
          // Triggered only once token is there
          res = await getOrderByToken(this.values.token)
        }
        const data = await res.json()
        const filteredData = omit(data, omitKeys)

        if (res.ok) {
          this.globalError = null
          // State changes: add values from the API
          this.values = { ...this.values, ...filteredData }

          if (this.isAbandonedBasket()) {
            // State changes: override values for the AC variants
            this.values = {
              ...this.values,
              activeOffer: null,
              exitIntent: false,
              isExitIntentPopupShown: false,
            }
          }

          if (this.values.ffInitialFetch) {
            const initialValues = getInitialValues()
            const queryParamValues = {
              ffSessionId: this.values.ffSessionId,
              ffOrigin: this.values.ffOrigin,
              ffOriginPath: this.values.ffOriginPath,
              ffOfferPPOId: this.values.ffOfferPPOId,
              ffExitIntentPath: this.values.ffExitIntentPath,
              activeOffer: this.values.activeOffer,
            }
            // State changes:
            // - intial dataStore values
            // - v3 BE data
            // - FF query params
            // - Default override values needed for pipe
            this.values = {
              ...initialValues,
              ...this.values,
              ...queryParamValues,
              ffInitialFetch: false,
              isExitIntentPopupShown: false,
            }
          }

          // Store in local storage
          this.writeToLocalStorage(this.values)
        } else {
          this.globalError = data.error
        }
      } catch (error) {
        this.globalError = error
        Bugsnag.notify(new Error("[Common] Error during data pulling"), (event) => {
          event.addMetadata("details", {
            originalError: error.message,
            stack: error.stack,
          })
        })
        throw error
      }
    }
  },

  // Push data store to backend API
  async push({ payloadExtraData, isSilentMode } = {}) {
    const apiUrl = pipelineConfig.orderApiUrl
    const options = {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        ...this.values,
        ...payloadExtraData,
      }),
    }

    if (this.values.token) {
      options.method = "PATCH"
      options.headers["x-token"] = this.values.token
    }

    try {
      this.globalError = null
      this.emailValidationError = null

      const res = await fetch(apiUrl, options)
      const data = await res.json()

      if (data.token) {
        this.values.token = data.token
      }

      if (isSilentMode) return

      if (res.ok) {
        this.globalError = null
        this.emailValidationError = null

        Object.entries(data).forEach(([key, value]) => {
          if (key !== "path") this.values[key] = value
        })

        this.values.isEmailStepCompleted = Boolean(this.values.email)
      } else {
        if (
          data.code === INVALID_DOMAIN_CODE ||
          data.error === EMAIL_VALIDATION_ERROR ||
          data.code === EMAIL_VALIDATION_CODE
        ) {
          this.emailValidationError = data
        } else {
          this.globalError = data.error
        }
      }
    } catch (error) {
      if (isSilentMode) return

      this.globalError = error
      Bugsnag.notify(error)
    }

    return !this.globalError && !this.emailValidationError
  },

  // Inherited from the u-series - tracks analytics data for the page status
  async trackStatusForAnalytics(status) {
    if (status) {
      this.values.status = status
      this.push({ payloadExtraData: { path: window.location.pathname }, isSilentMode: true })
    }
  },

  getWeightToLose() {
    const { weightKg, targetWeightKg, weightLbs, targetWeightLbs } = this.values

    const validWeightKg = parseFloat(weightKg) || 0
    const validTargetWeightKg = parseFloat(targetWeightKg) || 0
    const validWeightLbs = parseFloat(weightLbs) || 0
    const validTargetWeightLbs = parseFloat(targetWeightLbs) || 0

    const isMetric = this.isMetric()

    const weightToLose = isMetric
      ? validWeightKg - validTargetWeightKg
      : validWeightLbs - validTargetWeightLbs

    return isNaN(weightToLose) ? 0 : weightToLose
  },
})
